export const BODY_THEME_CLASS_NAME = 'themeClass'

export type ThemeConfigValue = string | number | null | undefined

export type ThemeConfig = Record<string, ThemeConfigValue>

export type NormalizedThemeConfig = {
  entries: Array<[string, string]>
  signature: string
}

type OriginalProperty = {
  existed: boolean
  priority: string
  value: string
}

let ownerSequence = 0
let activeOwnerId: number | null = null
let activeSignature: string | null = null
let activeKeys = new Set<string>()
const originalProperties = new Map<string, OriginalProperty>()

const INVALID_THEME_VALUE = /^(?:undefined|null|nan)(?:px)?$/i

function getBody() {
  if (typeof document === 'undefined') return null
  return document.body || null
}

function hasInlineProperty(style: CSSStyleDeclaration, property: string) {
  for (let index = 0; index < style.length; index += 1) {
    if (style.item(index) === property) return true
  }
  return false
}

function rememberOriginalProperty(body: HTMLElement, property: string) {
  if (originalProperties.has(property)) return

  originalProperties.set(property, {
    existed: hasInlineProperty(body.style, property),
    priority: body.style.getPropertyPriority(property),
    value: body.style.getPropertyValue(property),
  })
}

function restoreProperty(body: HTMLElement, property: string) {
  const original = originalProperties.get(property)
  if (!original?.existed) {
    body.style.removeProperty(property)
    return
  }

  body.style.setProperty(property, original.value, original.priority)
}

function clearBodyTheme(body: HTMLElement) {
  originalProperties.forEach((_original, property) => {
    restoreProperty(body, property)
  })
  body.classList.remove(BODY_THEME_CLASS_NAME)
  originalProperties.clear()
  activeKeys.clear()
  activeSignature = null
}

function normalizeThemeValue(value: ThemeConfigValue) {
  if (value === null || value === undefined) return null
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : null
  }

  const normalized = value.trim()
  if (!normalized || INVALID_THEME_VALUE.test(normalized)) return null
  return normalized
}

export function normalizeThemeConfig(
  themeConfig?: ThemeConfig,
): NormalizedThemeConfig {
  const entries = Object.keys(themeConfig || {})
    .filter((property) => property.startsWith('--'))
    .map((property): [string, string] | null => {
      const value = normalizeThemeValue(themeConfig?.[property])
      return value === null ? null : [property, value]
    })
    .filter((entry): entry is [string, string] => entry !== null)
    .sort(([left], [right]) => left.localeCompare(right))

  return {
    entries,
    signature: JSON.stringify(entries),
  }
}

export function createBodyThemeOwner() {
  ownerSequence += 1
  return ownerSequence
}

export function updateBodyTheme(
  ownerId: number,
  themeConfig: NormalizedThemeConfig,
) {
  const body = getBody()
  if (!body) return

  if (activeSignature === themeConfig.signature) {
    activeOwnerId = ownerId
    if (themeConfig.entries.length > 0) {
      body.classList.add(BODY_THEME_CLASS_NAME)
    } else {
      body.classList.remove(BODY_THEME_CLASS_NAME)
    }
    return
  }

  if (themeConfig.entries.length === 0) {
    clearBodyTheme(body)
    activeOwnerId = ownerId
    activeSignature = themeConfig.signature
    return
  }

  const nextKeys = new Set(themeConfig.entries.map(([property]) => property))
  activeKeys.forEach((property) => {
    if (!nextKeys.has(property)) restoreProperty(body, property)
  })

  themeConfig.entries.forEach(([property, value]) => {
    rememberOriginalProperty(body, property)
    body.style.setProperty(property, value)
  })

  body.classList.add(BODY_THEME_CLASS_NAME)
  activeKeys = nextKeys
  activeOwnerId = ownerId
  activeSignature = themeConfig.signature
}

export function releaseBodyTheme(ownerId: number) {
  if (activeOwnerId !== ownerId) return

  const body = getBody()
  if (body) clearBodyTheme(body)
  activeOwnerId = null
  activeSignature = null
}

export function resetBodyThemeManagerForTest() {
  const body = getBody()
  if (body) clearBodyTheme(body)
  originalProperties.clear()
  activeKeys.clear()
  activeOwnerId = null
  activeSignature = null
  ownerSequence = 0
}
